import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
  DIAGNOSTICS_REPORT_TYPES,
  FREQUENCY,
  FREQUENCY_MONTHLY,
  FREQUENCY_WEEKLY,
  OTHER_SCHEDULED_REPORT_TYPES,
  COACHING_REPORT_TYPES,
  TIME,
  REQUIRED_FIELDS_FOR_REPORT_TYPES,
  ASSET_DRIVER_FILTER,
} from '@app-reports/constants/reports.constants';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { MatChipInputEvent, MatChipList } from '@angular/material/chips';
import { Observable, of, Subject } from 'rxjs';
import { finalize, map, skip, startWith, takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CreateScheduleBody,
  CreateScheduleRouteParams,
  FormAction,
  FormData,
  PatchScheduleBody,
  ScheduleConfig,
  ScheduledReportRow,
} from '@app-reports/models/reports.model';
import { DataService } from '@app-core/services/data/data.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { CanComponentDeactivate } from '@app-core/guards/can-deactivate/can-deactivate.guard';
import { AssetAutoCompleteResponseObject } from '@app-core/models/core.model';
import { TranslateService } from '@ngx-translate/core';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { asymmetricArrayDiff } from '@app-core/models/dirty-check';
import { UserRoleManageService } from '@app-user-management/service/user-role-manage.service';
import { AccessService } from '@app-core/services/access/access.service';
import { ValidatorService } from '@app-shared/services/validator/validator.service';
import { isEmail } from 'validator';

@Component({
  selector: 'app-create-schedule',
  templateUrl: './create-schedule.component.html',
  styleUrls: ['./create-schedule.component.scss'],
})
export class CreateScheduleComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  public form: FormGroup;
  public actionName = FormAction.Create;
  private readonly scheduleNameRegex = /^[a-zA-Z0-9@_\-][a-zA-Z0-9@_\- ]{3,}$/;
  private readonly minLengthScheduleName = 4;
  private readonly maxLengthScheduleName = 80;
  public readonly FREQUENCY = FREQUENCY;
  public readonly FREQUENCY_WEEKLY = FREQUENCY_WEEKLY;
  public readonly FREQUENCY_MONTHLY = FREQUENCY_MONTHLY;
  public readonly TIME = TIME;
  public readonly FormAction = FormAction;
  private scheduleDetails: ScheduledReportRow;
  private scheduleId: string;
  public selectedReportType;
  public recipientEmails: string[] = [];
  public filteredEmails$: Observable<string[]>;
  public loader = false;
  public createScheduleLoader = false;
  public canDeactivateFlag: boolean = false;
  private ngUnsubscribe = new Subject<void>();

  public coachingReportTypes = Object.values(COACHING_REPORT_TYPES);
  public diagnosticsReportTypes = Object.values(DIAGNOSTICS_REPORT_TYPES);
  public otherScheduledReportTypes = Object.values(OTHER_SCHEDULED_REPORT_TYPES);

  public allScheduledReportTypesCombined = [...this.coachingReportTypes, ...this.diagnosticsReportTypes, ...this.otherScheduledReportTypes];
  public assetDriverFilter = ASSET_DRIVER_FILTER;
  public scheduledReportsTags: any[] = [];
  private ALL_DRIVERS_ID = 'All Drivers';
  public readonly optionalFormFields = ['reportSubtype', 'duration', 'frequency', 'sendDay', 'sendTime', 'driverId', 'asset', 'tagIds'];
  private requiredFieldsForReportTypes = REQUIRED_FIELDS_FOR_REPORT_TYPES;
  public scheduledReportAdditionalDetails = {};
  public entityType: string[] = [];
  private ssoUserHash: string = '';

  @ViewChild('emailInput', { static: false }) public emailInput: ElementRef;
  @ViewChild('chipList', { static: false }) public chipList: MatChipList;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  constructor(
    private fb: FormBuilder,
    private location: Location,
    private reportsService: ReportsService,
    private route: ActivatedRoute,
    private router: Router,
    public dataService: DataService,
    private snackbarService: SnackBarService,
    private dialog: MatDialog,
    public translate: TranslateService,
    private userService: UserRoleManageService,
    private accessService: AccessService,
    private validatorService: ValidatorService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    this.generateForm();
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: CreateScheduleRouteParams) => {
      const { action, scheduleId, reportType, includeInsignificantTrips = '', includeInsignificantPoints = '' } = params || {};
      this.actionName = action || FormAction.Create;
      if ((this.actionName === FormAction.Edit || this.actionName === FormAction.Duplicate) && scheduleId) {
        this.scheduleId = scheduleId;
        if (scheduleId === this.reportsService.scheduleDetails?.scheduleId) {
          this.processSchedule(this.reportsService.scheduleDetails);
        } else {
          this.getSchedule();
        }
      } else if (this.actionName === FormAction.Create && reportType) {
        this.form?.patchValue({ reportType });
        this.form?.get('reportType')?.disable();
        this.reportTypeChange();
        includeInsignificantTrips && (this.scheduledReportAdditionalDetails['includeInsignificantTrips'] = includeInsignificantTrips);
        includeInsignificantPoints && (this.scheduledReportAdditionalDetails['includeInsignificantPoints'] = includeInsignificantPoints);
      }
    });

    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.canDeactivateFlag = true;
      this.navigateBack();
    });

    this.ssoUserHash = this.accessService.getLoginInfo().userHash;
    this.coachingReportTypes = this.reportsService.getReportTypes(Object.values(COACHING_REPORT_TYPES));
    this.diagnosticsReportTypes = this.reportsService.getReportTypes(Object.values(DIAGNOSTICS_REPORT_TYPES));
  }

  private scheduleNameTrimmedLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim() || '';
      const length = value.length;
      return length >= this.minLengthScheduleName && length < this.maxLengthScheduleName ? null : { invalidScheduleNameLength: true };
    };
  }

  validateEmailNoSpace(value: string): void {
    const trimmedValue = value;
    if (trimmedValue.includes(' ')) {
      this.form.get('email')?.setErrors({ spacesNotAllowed: true });
      this.chipList.errorState = true;
    } else {
      this.form.get('email')?.setErrors(null);
      this.chipList.errorState = false;
    }
  }

  private updateEntities() {
    const reportType = this.form.get('reportType')?.value;
    let entities = [];
    if (reportType === 'TRIPS') {
      entities = ['asset', 'driver', 'trip'];
    } else if (reportType === 'FLEET_SAFETY') {
      entities = ['asset', 'driver'];
    } else if (reportType === 'EVENT_LIST' || reportType === 'EVENT_COUNT') {
      entities = ['asset', 'driver', 'event'];
    }

    this.entityType = entities;
  }

  private generateForm() {
    this.form = this.fb.group({
      scheduleName: [
        '',
        [
          Validators.required,
          Validators.minLength(this.minLengthScheduleName),
          Validators.maxLength(this.maxLengthScheduleName),
          Validators.pattern(this.scheduleNameRegex),
          this.scheduleNameTrimmedLengthValidator(),
        ],
      ],
      reportType: ['', [Validators.required]],
      reportSubtype: [''],
      duration: [''],
      frequency: [''],
      sendDay: [1],
      sendTime: [''],
      email: ['', [this.validatorService.customEmailValidator()]],
      filterType: ['driverFilter', []],
      driverId: [this.ALL_DRIVERS_ID],
      asset: [null, []],
      tagIds: ['', []],
    });

    this.reportsService.updateReportsDurationOptions();
    this.filterEmails();
    this.updateValidators();
  }

  private getSchedule() {
    if (!this.scheduleId) {
      return;
    }

    this.loader = true;

    this.reportsService
      .getScheduleReportDetails(this.scheduleId)
      .pipe(
        finalize(() => (this.loader = false)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (schedule: ScheduledReportRow) => {
          if (schedule) {
            if (schedule.reportType === 'COACHING_SESSION') {
              schedule.scheduleConfiguration.frequency = 'IMMEDIATE';
            }
            this.reportsService.scheduleDetails = schedule;
            this.processSchedule(schedule);
          }
        },
        () => {
          this.snackbarService.failure(this.translate.instant('Failed to get schedule details'));
          this.actionName = FormAction.Create;
        }
      );
  }

  private processSchedule(schedule: ScheduledReportRow) {
    this.scheduleDetails = schedule;
    const formData = this.parseRow(this.scheduleDetails);
    this.patchFormData(formData);
    this.updateValidators();
    this.updateEntities();
  }

  private parseRow(row: ScheduledReportRow) {
    const { scheduleId, scheduleName, reportType } = row || {};
    const {
      frequency,
      sendTimeUTC,
      reportDurationRange: duration,
      dayOfTheSchedule,
      additionalEmails: email,
      assetId,
      driverId,
      tagIds = [],
    } = row?.scheduleConfiguration || {};
    const reportSubtype = this.reportsService.getSubtypeKey(reportType, row?.scheduleConfiguration) || 'ALL';
    const { sendTime, sendDay } = this.getUserTime(sendTimeUTC, dayOfTheSchedule, frequency) || {};
    const formData: FormData = {
      scheduleId,
      scheduleName,
      reportType,
      frequency,
      sendTime,
      duration,
      sendDay,
      email,
      reportSubtype,
      asset: { assetId },
      driverId,
      tagIds,
    };

    return formData;
  }

  private patchFormData(formData: FormData) {
    const {
      scheduleName,
      reportType,
      reportSubtype,
      duration,
      frequency,
      sendDay,
      sendTime,
      email: emailList,
      asset,
      driverId,
      tagIds,
    } = formData;

    const report = this.allScheduledReportTypesCombined.find((report) => report.key === reportType) || null;
    const isValid = (type, formValue): boolean => {
      return formValue && report?.[type]?.some((val) => val.key === formValue);
    };
    this.selectedReportType = report;
    this.scheduledReportsTags = this.getTags(tagIds) || [];

    this.form.patchValue({
      ...(this.actionName === FormAction.Duplicate ? { scheduleName: 'Copy_' + scheduleName } : { scheduleName }),
      ...(report ? { reportType } : {}),
      ...(isValid('subtypes', reportSubtype) ? { reportSubtype } : {}),
      ...(isValid('reportDuration', duration) ? { duration } : {}),
      ...(isValid('frequency', frequency) ? { frequency } : {}),
      ...(typeof sendDay === 'number' && (frequency === FREQUENCY.WEEKLY.key || frequency === FREQUENCY.MONTHLY.key) ? { sendDay } : {}),
      ...(isValid('frequency', frequency) && TIME.includes(sendTime) ? { sendTime } : {}),
      ...(report?.supportsAssetDriverFilter
        ? driverId
          ? { filterType: 'driverFilter' }
          : asset.assetId
          ? { filterType: 'assetFilter' }
          : { filterType: 'driverFilter' }
        : {}),
      ...(report?.supportsAssetDriverFilter ? (driverId ? { driverId } : asset.assetId ? { asset } : {}) : {}),
      ...(report?.supportsTagsFilter ? { tagIds } : {}),
    });

    this.recipientEmails = emailList && emailList.length > 0 ? emailList?.filter((email) => isEmail(email)) : [];

    if (this.actionName === FormAction.Edit) {
      this.form.get('reportType')?.disable();
    }
  }

  public reportTypeChange(): void {
    const selectedReportTypeKey = this.form?.get('reportType').value;
    this.selectedReportType = this.allScheduledReportTypesCombined.find((reportType) => reportType.key === selectedReportTypeKey) || null;
    this.updateValidators();
    this.updateEntities();
    this.form.patchValue({
      reportSubtype: '',
      duration: '',
      frequency: '',
      sendDay: 1,
      sendTime: '',
      tagIds: [],
      asset: null,
      driverId: this.ALL_DRIVERS_ID,
      email: '',
    });
    this.recipientEmails = [];
  }

  public addEmail(event: MatChipInputEvent): void {
    const email = (event.value || '').trim();
    this.chipList && (this.chipList.errorState = false);

    if (!isEmail(email)) {
      this.chipList && (this.chipList.errorState = true);
      return;
    }

    if (email && !this.recipientEmails.includes(email)) {
      this.recipientEmails.push(email);
    }

    event.chipInput!.clear();
    this.form.patchValue({
      email: '',
    });
    this.form.markAsDirty();
  }

  public removeEmail(email: string): void {
    const index = this.recipientEmails.indexOf(email);

    if (index >= 0) {
      this.recipientEmails.splice(index, 1);
    }

    this.emailInput.nativeElement.value = '';
    this.form.patchValue({
      email: '',
    });
    this.form.markAsDirty();
  }

  public filterEmails() {
    this.filteredEmails$ = this.form.get('email').valueChanges.pipe(
      startWith(''),
      tap(() => this.chipList && (this.chipList.errorState = false)),
      map((searchString: string) => this._filter(searchString))
    );
  }

  private _filter(searchString: string) {
    const value = searchString?.trim()?.toLowerCase();
    return this.recipientEmails.filter((email) => email.toLowerCase().includes(value));
  }

  public navigateBack() {
    this.location.back();
  }

  public frequencyChange(): void {
    this.form.patchValue({
      sendDay: 1,
      sendTime: '',
    });
  }

  private getUTCTime() {
    const sendDay = this.form?.get('sendDay').value as number;
    const time = this.form?.get('sendTime').value;
    const frequency = this.form?.get('frequency').value;

    if (!time || sendDay === undefined) {
      return;
    }
    return this.reportsService.getUTCTime(time, sendDay, frequency);
  }

  private getUserTime(sendTimeUTC, dayOfTheSchedule, frequency) {
    const sendDay = typeof dayOfTheSchedule === 'number' ? dayOfTheSchedule : null;
    const time = TIME.includes(sendTimeUTC) ? sendTimeUTC : null;
    frequency = Object.values(FREQUENCY)?.some((f) => f.key === frequency) ? frequency : null;
    if ((sendDay === null && frequency !== this.FREQUENCY.DAILY.key) || !time || !frequency) {
      return;
    }
    return this.reportsService.getUserTime(time, sendDay, frequency);
  }

  get isFrequencyDaily(): boolean {
    return this.form?.get('frequency')?.value === this.FREQUENCY.DAILY.key;
  }

  get isFrequencyWeekly(): boolean {
    return this.form?.get('frequency')?.value === this.FREQUENCY.WEEKLY.key;
  }

  get isFrequencyMonthly(): boolean {
    return this.form.get('frequency')?.value === this.FREQUENCY.MONTHLY.key;
  }

  get isFrequencyImmediate(): boolean {
    return this.form.get('frequency')?.value === this.FREQUENCY.IMMEDIATE.key;
  }

  public onSubmit() {
    this.createScheduleLoader = true;
    const {
      scheduleName,
      reportType,
      reportSubtype,
      duration,
      frequency,
      filterType,
      driverId,
      asset,
      tagIds = [],
    } = this.form.getRawValue();

    const isValid = (type, formValue): boolean => {
      if (type === 'frequency' && formValue === FREQUENCY.IMMEDIATE.key) {
        return false;
      }
      return formValue && this.selectedReportType?.[type]?.some((val) => val.key === formValue);
    };

    const { sendTimeUTC, dayOfTheSchedule } = (isValid('frequency', frequency) && this.getUTCTime()) || {};

    let selectedDriverId = driverId !== this.ALL_DRIVERS_ID && filterType === 'driverFilter' ? driverId : null;
    let selectedAssetId = asset?.assetId && filterType === 'assetFilter' ? asset.assetId : null;
    const selectedTagIds = tagIds.length > 0 ? tagIds : null;

    const scheduleConfiguration: ScheduleConfig = {
      ...(isValid('frequency', frequency) ? { frequency } : {}),
      ...(isValid('frequency', frequency) && sendTimeUTC ? { sendTimeUTC } : {}),
      ...(isValid('reportDuration', duration) ? { reportDurationRange: duration } : {}),
      ...(isValid('frequency', frequency) && (this.isFrequencyMonthly || this.isFrequencyWeekly) ? { dayOfTheSchedule } : {}),
      ...(this.recipientEmails.length > 0 ? { additionalEmails: this.recipientEmails } : { additionalEmails: null }),
      ...(isValid('subtypes', reportSubtype) ? this.reportsService.getSubtypeParams(reportType, reportSubtype) : {}),
      ...(this.selectedReportType?.supportsTagsFilter ? (selectedTagIds?.length > 0 ? { tagIds: selectedTagIds } : { tagIds: null }) : {}),
      ...(this.selectedReportType?.supportsAssetDriverFilter && filterType === 'driverFilter'
        ? selectedDriverId
          ? { driverId: selectedDriverId, assetId: null }
          : driverId === this.ALL_DRIVERS_ID
          ? { driverId: null, assetId: null }
          : { driverId: null }
        : {}),
      ...(this.selectedReportType?.supportsAssetDriverFilter && filterType === 'assetFilter'
        ? selectedAssetId
          ? { assetId: selectedAssetId, driverId: null }
          : { assetId: null }
        : {}),
    };

    this.addAdditionalParams(scheduleConfiguration);

    let api$;
    if (this.actionName === FormAction.Create || this.actionName === FormAction.Duplicate) {
      const body: CreateScheduleBody = {
        scheduleName,
        reportType,
        scheduleConfiguration,
      };
      api$ = this.reportsService.createScheduledReports(body);

      if (this.ssoUserHash) {
        this.userService
          .getConsentTimeFromSSOUser(encodeURIComponent(this.ssoUserHash))
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(
            (consentTime: string) => {
              if (!consentTime) {
                this.handleConsentDialog(api$, body, frequency);
              } else {
                this.handleApiCall(api$, body, frequency);
              }
            },
            (error) => {
              if (error.status === 404) {
                // Handle 404 error by opening the consent dialog
                this.handleConsentDialog(api$, body, frequency);
              }
            }
          );
      } else {
        this.handleApiCall(api$, body, frequency);
      }
    } else if (this.actionName === FormAction.Edit) {
      const body: PatchScheduleBody = {
        scheduleName,
        reportType,
        scheduleConfiguration,
      };
      api$ = this.reportsService.updateScheduledReport(this.scheduleId, body);
      this.handleApiCall(api$, body, frequency);
    }
  }

  private handleConsentDialog(api$, body: PatchScheduleBody, frequency: string) {
    const ssoConsentDialogRef = this.reportsService.openSSOConsentDialog();
    ssoConsentDialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handleApiCall(api$, body, frequency);
      });
  }

  public handleApiCall(api$, body: PatchScheduleBody, frequency: string) {
    api$
      .pipe(
        finalize(() => {
          this.createScheduleLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.mixpanelService.track('Scheduled report submitted', {
            'Report Type': body.reportType,
            Frequency: frequency || '',
          });
          if (this.actionName === FormAction.Create || this.actionName === FormAction.Duplicate) {
            this.snackbarService.success(`"${body.scheduleName}" ${this.translate.instant('has been created successfully.')}`);
          } else if (this.actionName === FormAction.Edit) {
            this.snackbarService.success(`"${body.scheduleName}" ${this.translate.instant('has been updated successfully.')}`);
          } else if (this.actionName === FormAction.Duplicate) {
            this.snackbarService.success(`"${body.scheduleName}" ${this.translate.instant('has been duplicated successfully.')}`);
          }

          this.canDeactivateFlag = true;
          this.router.navigate(['/reports'], {
            queryParams: {
              tab: 'scheduled',
            },
          });
        },
        (err) => {
          if (err.status === 409) {
            this.snackbarService.failure(this.translate.instant('Schedule name already exists. Please enter another name'));
          } else if (err.status === 406) {
            this.snackbarService.failure(
              `${this.translate.instant(
                'You have reached maximum number of schedules for this report. Please review your existing schedules or choose a different report type.'
              )}`
            );
          } else {
            this.snackbarService.failure(`${this.translate.instant('Failed to create schedule, please try again later.')}`);
          }
        }
      );
  }

  public canDeactivate(): Observable<boolean> {
    if (this.form.touched && !this.canDeactivateFlag) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        position: { top: '24px' },
        width: '400px',
        data: {
          title: this.translate.instant('Schedule Report'),
          content: this.translate.instant('Are you sure you want to discard the form?'),
          actions: [
            { actionButton: this.translate.instant('CANCEL'), dialogCloseValue: false },
            { actionButton: this.translate.instant('DISCARD'), dialogCloseValue: true, primary: true },
          ],
        },
      });
      return dialogRef.afterClosed();
    }
    return of(true);
  }

  public driverChanged(driverId = '') {
    this.form.patchValue({ driverId });
    this.form.markAsDirty();
  }

  public assetChanged(asset: AssetAutoCompleteResponseObject) {
    this.form.patchValue({ asset });
    this.form.markAsDirty();
  }

  public selectedTags(tagIds) {
    this.form?.patchValue({
      tagIds: tagIds || [],
    });
    const eventTagsIds: Number[] = (this.scheduledReportsTags || []).map((t) => t.tagId);
    const diff = asymmetricArrayDiff<Number>(tagIds || [], eventTagsIds);
    const isDiff = Boolean(diff.length);
    if (isDiff) {
      this.form.markAsDirty();
    }
  }

  public getTags(tagIds = []) {
    const tags = tagIds?.map((tagId) => ({ tagId })) || [];
    return tags;
  }

  clearValidators() {
    this.optionalFormFields.forEach((field) => {
      this.form.get(field)?.clearValidators();
    });
  }

  updateValidators() {
    const reportKey = this.selectedReportType?.key;
    this.clearValidators();
    this.requiredFieldsForReportTypes[reportKey]?.forEach((field) => {
      this.form.get(field)?.setValidators([Validators.required]);
    });
  }

  addAdditionalParams(scheduleConfiguration) {
    const additionalParams = this.selectedReportType?.additionalParams;
    if (additionalParams) {
      additionalParams.forEach((param) => {
        const val = this.getAdditionalParamValue(param) || '';
        if (val) {
          scheduleConfiguration[param] = val;
        }
      });
    }
  }

  getAdditionalParamValue(param) {
    if (!param) {
      return;
    }
    let returnVal;

    switch (param) {
      case 'unit':
        const unit = this.dataService._currentMetricUnit.value;
        returnVal = unit ? (unit === 'Miles' ? 'mi' : 'km') : null;
        break;

      case 'language':
        returnVal = this.dataService._currentLanguage.value || null;
        break;

      case 'dateFormat':
        returnVal = this.dataService._currentDateFormat.value || null;
        break;

      case 'includeInsignificantTrips':
      case 'includeInsignificantPoints':
        const val = this.scheduledReportAdditionalDetails?.[param];
        returnVal = val !== undefined ? val : null;
        break;

      default:
        returnVal = null;
    }
    return returnVal;
  }

  toggleAutoComplete() {
    if (this.autocomplete.panelOpen) {
      this.autocomplete.closePanel();
    } else {
      this.autocomplete.openPanel();
    }
  }
  onOptionSelected() {
    this.emailInput.nativeElement.value = '';
    this.form.patchValue({
      email: '',
    });
    this.autocomplete.closePanel();
  }

  onEnterKey(event: KeyboardEvent) {
    event.preventDefault();
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
